import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NotebookPen, Sparkles } from 'lucide-react';
import EmptyState from 'components/ui/EmptyState';
import Skeleton from 'components/ui/skeleton';
import { SegmentedTabs } from 'components/ui/data-table';
import { PlanCheckoutModal } from 'components/portal/PlanCheckoutModal';
import { upsellClass, upsellIconClass, upsellSurfaceClass } from 'components/portal/upsell';
import { cn } from 'lib/utils';
import { useMistakes, type Mistake, type MistakeSkill } from 'modules/mistakes';
import MistakeCard from './MistakeCard';
import { PortalHeading } from './PortalHeading';

type Filter = 'all' | MistakeSkill;
const SKILL_ORDER: MistakeSkill[] = ['listening', 'reading', 'sat_rw', 'sat_math'];

interface StatProps {
  label: string;
  value: React.ReactNode;
  tone?: 'plain' | 'amber';
}

function Stat({ label, value, tone = 'plain' }: StatProps) {
  return (
    <div
      className={cn(
        'rounded-[18px] border px-4 py-3.5 md:px-[18px] md:py-4',
        tone === 'amber' ? 'border-[#F3DCA0] bg-[#FFF4D6] dark:border-amber-400/30 dark:bg-amber-400/10' : 'border-border bg-card',
      )}
    >
      <dt className={cn('font-mono text-[10.5px] font-bold uppercase tracking-[0.14em] md:text-[11px]', tone === 'amber' ? 'text-[#92400E] dark:text-amber-300' : 'text-muted-foreground')}>
        {label}
      </dt>
      <dd className={cn('mt-1 font-display text-2xl font-extrabold tracking-[-0.04em] tabular-nums md:text-[30px]', tone === 'amber' && 'text-[#92400E] dark:text-amber-300')}>
        {value}
      </dd>
    </div>
  );
}

/** Where the open mistakes pile up, most first: one red bar per skill. */
function WeakSkills({ items }: { items: Mistake[] }) {
  const { t } = useTranslation();
  const counts = SKILL_ORDER.map((skill) => ({ skill, count: items.filter((m) => m.skill === skill).length }))
    .filter(({ count }) => count > 0)
    .sort((a, b) => b.count - a.count);
  if (counts.length === 0) return null;
  const peak = counts[0].count;
  return (
    <section aria-labelledby="weak-skills" className="rounded-[22px] border border-border bg-card p-5 md:p-[22px]">
      <h2 id="weak-skills" className="font-display text-[19px] font-extrabold tracking-[-0.03em] text-foreground">{t('mistakes.weak_title')}</h2>
      <ul>
        {counts.map(({ skill, count }) => (
          <li key={skill} className="mt-3.5">
            <div className="flex justify-between text-[13px] font-semibold text-foreground">
              <span>{t(`mistakes.skill_${skill}`)}</span>
              <span className="tabular-nums">{t('mistakes.count_short', { count })}</span>
            </div>
            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-border">
              <div className="h-full rounded-full bg-[#B42318]" style={{ width: `${(count / peak) * 100}%` }} />
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Shown while some mistakes' explanations are still locked behind an unopened analysis. */
function UnlockPanel({ lockedTests, onOpen }: { lockedTests: number; onOpen: () => void }) {
  const { t } = useTranslation();
  return (
    <section aria-labelledby="unlock-title" className={cn('rounded-[22px] border p-5 md:p-[22px]', upsellSurfaceClass)}>
      <span className={cn(upsellIconClass, 'h-[42px] w-[42px] rounded-[13px]')}>
        <Sparkles className="h-5 w-5" aria-hidden />
      </span>
      <h2 id="unlock-title" className="mt-3.5 font-display text-lg font-extrabold tracking-[-0.03em] text-foreground">{t('mistakes.unlock_title')}</h2>
      <p className="mb-3.5 mt-2 text-[13px] leading-relaxed text-muted-foreground">{t('mistakes.unlock_body', { count: lockedTests })}</p>
      <button type="button" onClick={onOpen} className={cn('inline-flex h-12 w-full items-center justify-center rounded-xl text-sm font-bold', upsellClass)}>
        {t('mistakes.unlock_cta')}
      </button>
    </section>
  );
}

/** Wrong answers from the learner's IELTS and SAT attempts, to answer again and put right for points. */
export default function Mistakes() {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = useMistakes();
  const [filter, setFilter] = useState<Filter>('all');
  const [plansOpen, setPlansOpen] = useState(false);
  const items = useMemo(() => data?.items ?? [], [data]);
  const skills = SKILL_ORDER.filter((skill) => items.some((m) => m.skill === skill));
  const shown = filter === 'all' ? items : items.filter((m) => m.skill === filter);
  const lockedTests = useMemo(() => new Set(items.filter((m) => m.explanationLocked).map((m) => m.testId)).size, [items]);

  if (isLoading) {
    return (
      <div className="space-y-3" aria-hidden>
        {[0, 1, 2].map((i) => <Skeleton key={i} className="h-40 w-full rounded-lg" />)}
      </div>
    );
  }
  if (isError) {
    return (
      <EmptyState
        icon={<NotebookPen />}
        title={t('mistakes.load_failed')}
        secondaryAction={{ label: t('retry'), onClick: () => void refetch() }}
      />
    );
  }
  if (items.length === 0) {
    return (
      <EmptyState
        icon={<NotebookPen />}
        title={t('mistakes.empty_title')}
        description={data?.mastered
          ? `${t('mistakes.all_learned', { count: data.mastered })}${data.points ? ` ${t('mistakes.points_total', { points: data.points })}` : ''}`
          : t('mistakes.empty_body')}
      />
    );
  }

  return (
    <div className="flex flex-col">
      <PortalHeading
        title={t('mistakes.tab')}
        description={t('mistakes.subtitle')}
        aside={
          skills.length > 1 ? (
            <SegmentedTabs<Filter>
              aria-label={t('mistakes.filter')}
              className="h-10 max-w-full self-start overflow-x-auto md:self-auto"
              options={[
                { value: 'all' as const, label: t('mistakes.skill_all') },
                ...skills.map((skill) => ({ value: skill, label: t(`mistakes.skill_${skill}`) })),
              ]}
              value={filter}
              onChange={setFilter}
            />
          ) : undefined
        }
      />
      <section aria-labelledby="mistakes-heading" className="mt-5 md:mt-6">
        <h2 id="mistakes-heading" className="sr-only">{t('mistakes.title', { count: items.length })}</h2>
        <dl className="grid grid-cols-2 gap-2.5 md:grid-cols-4 md:gap-3.5">
          <Stat label={t('mistakes.stat_open')} value={items.length} />
          <Stat label={t('mistakes.stat_learned')} value={data?.mastered ?? 0} />
          <Stat label={t('mistakes.stat_fixed')} value={data?.fixed ?? 0} />
          <Stat label={t('mistakes.stat_points')} value={`+${data?.points ?? 0}`} tone="amber" />
        </dl>
        <div className="mt-5 grid items-start gap-6 md:mt-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <ul className="space-y-3.5">
            {shown.map((mistake) => <MistakeCard key={mistake.key} mistake={mistake} />)}
          </ul>
          <aside className="flex flex-col gap-4 lg:sticky lg:top-24">
            <WeakSkills items={items} />
            {lockedTests > 0 && <UnlockPanel lockedTests={lockedTests} onOpen={() => setPlansOpen(true)} />}
          </aside>
        </div>
      </section>
      {plansOpen && <PlanCheckoutModal open onOpenChange={setPlansOpen} />}
    </div>
  );
}
